import api from './api';
import type {
  SpecDocumentCategory,
  WorkflowStatusResponse,
  CanCreateResponse,
  DocumentDependenciesResponse,
  DocumentCategoryInfo,
} from '@/types';

// Type definitions
export type ArtifactType = 'code' | 'prototype' | 'asset' | 'config' | 'export' | 'generated' | 'upload' | 'document';
export type ArtifactStatus = 'active' | 'archived' | 'deleted';
export type SourceType = 'agent' | 'workflow' | 'user';

// Document-specific types (for artifact_type: 'document')
export type DocumentCategory =
  | 'prd'
  | 'trd'
  | 'api_spec'
  | 'db_schema'
  | 'user_flow'
  | 'design_system'
  | 'tasks'
  | 'requirements'
  | 'architecture'
  | 'testing'
  | 'deployment'
  | 'other';

export type DocumentStatus = 'draft' | 'review' | 'approved' | 'deprecated';

export interface DocumentOutlineItem {
  level: number;
  text: string;
}

export interface Artifact {
  id: string;
  name: string;
  artifact_type: ArtifactType;
  file_path: string | null;
  mime_type: string | null;
  content: string | null;
  storage_key: string | null;
  file_size: number | null;
  checksum: string | null;
  metadata: Record<string, unknown>;
  version: number;
  status: ArtifactStatus;
  source_type: SourceType | null;
  source_id: string | null;
  created_at: string;
  updated_at: string;
  // Computed attributes
  text_content: boolean;
  binary_content: boolean;
  file_extension: string | null;
  filename: string | null;
  directory: string | null;
  content_preview: string | null;
  content_lines: number;
  detected_language: string | null;
  created_by_name: string | null;
  from_agent: boolean;
  from_workflow: boolean;
  from_user: boolean;
  is_active: boolean;
  can_edit: boolean;
  can_download: boolean;
  file_size_display: string | null;
  // Document-specific fields (for artifact_type: 'document')
  category: DocumentCategory | null;
  slug: string | null;
  summary: string | null;
  outline: DocumentOutlineItem[] | null;
  tags: string[];
  doc_status: DocumentStatus | null;
  ai_generated: boolean;
  ai_model: string | null;
  ai_context: Record<string, unknown> | null;
  last_modified_by_name: string | null;
  // Document computed attributes
  is_document: boolean;
  word_count: number | null;
  reading_time_minutes: number | null;
  category_display: string | null;
  is_published: boolean;
  has_embeddings: boolean;
  needs_embedding: boolean;
  embedding_count: number;
  // Workflow fields
  is_essential: boolean;
  generation_order: number | null;
  generated_by_agent: string | null;
  // Confirmation fields
  confirmed: boolean;
  confirmed_at: string | null;
  confirmed_by_id: string | null;
  confirmed_by_name: string | null;
  confirmation_note: string | null;
  requires_confirmation: boolean;
}

export interface ArtifactFormData {
  name: string;
  artifact_type: ArtifactType;
  file_path?: string;
  mime_type?: string;
  content?: string;
  metadata?: Record<string, unknown>;
  // Document-specific fields
  category?: DocumentCategory;
  tags?: string[];
  doc_status?: DocumentStatus;
}

interface ArtifactListResponse {
  data: Artifact[];
  meta: {
    current_page: number;
    total_pages: number;
    total_count: number;
  };
}

interface ArtifactFilters {
  artifact_type?: ArtifactType;
  status?: ArtifactStatus;
  source_type?: SourceType;
  search?: string;
  // Document-specific filters
  category?: DocumentCategory;
  doc_status?: DocumentStatus;
  tag?: string;
  ai_generated?: boolean;
  fulltext?: string;
}

export const artifactService = {
  // Get all artifacts for a project
  async getArtifacts(
    projectId: string,
    filters: ArtifactFilters = {},
    page = 1,
    perPage = 50
  ): Promise<ArtifactListResponse> {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('per_page', String(perPage));

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });

    // Backend returns { artifacts: [...], pagination: {...} }
    const response = await api.get<{ artifacts: Artifact[]; pagination: { current_page: number; total_pages: number; total_count: number } }>(
      `/projects/${projectId}/artifacts?${params.toString()}`
    );
    // Transform to expected ArtifactListResponse format
    return {
      data: response.data.artifacts || [],
      meta: {
        current_page: response.data.pagination?.current_page || page,
        total_pages: response.data.pagination?.total_pages || 1,
        total_count: response.data.pagination?.total_count || 0,
      },
    };
  },

  // Get a single artifact
  async getArtifact(projectId: string, artifactId: string): Promise<Artifact> {
    const response = await api.get<{ artifact: Artifact }>(
      `/projects/${projectId}/artifacts/${artifactId}`
    );
    return response.data.artifact;
  },

  // Create a new artifact
  async createArtifact(projectId: string, data: ArtifactFormData): Promise<Artifact> {
    const response = await api.post<{ artifact: Artifact }>(
      `/projects/${projectId}/artifacts`,
      { artifact: data }
    );
    return response.data.artifact;
  },

  // Update an artifact
  async updateArtifact(
    projectId: string,
    artifactId: string,
    data: Partial<ArtifactFormData>
  ): Promise<Artifact> {
    const response = await api.patch<{ artifact: Artifact }>(
      `/projects/${projectId}/artifacts/${artifactId}`,
      { artifact: data }
    );
    return response.data.artifact;
  },

  // Delete an artifact
  async deleteArtifact(projectId: string, artifactId: string): Promise<void> {
    await api.delete(`/projects/${projectId}/artifacts/${artifactId}`);
  },

  // Archive an artifact
  async archiveArtifact(projectId: string, artifactId: string): Promise<Artifact> {
    const response = await api.post<{ artifact: Artifact }>(
      `/projects/${projectId}/artifacts/${artifactId}/archive`
    );
    return response.data.artifact;
  },

  // Restore an archived artifact
  async restoreArtifact(projectId: string, artifactId: string): Promise<Artifact> {
    const response = await api.post<{ artifact: Artifact }>(
      `/projects/${projectId}/artifacts/${artifactId}/restore`
    );
    return response.data.artifact;
  },

  // Duplicate an artifact
  async duplicateArtifact(
    projectId: string,
    artifactId: string,
    options?: { name?: string; file_path?: string }
  ): Promise<Artifact> {
    const response = await api.post<{ artifact: Artifact }>(
      `/projects/${projectId}/artifacts/${artifactId}/duplicate`,
      options
    );
    return response.data.artifact;
  },

  // Download artifact content
  async downloadArtifact(projectId: string, artifactId: string): Promise<Blob> {
    const response = await api.get(`/projects/${projectId}/artifacts/${artifactId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Upload a file as artifact
  async uploadArtifact(
    projectId: string,
    file: File,
    options?: { name?: string; artifact_type?: ArtifactType }
  ): Promise<Artifact> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.name) formData.append('name', options.name);
    if (options?.artifact_type) formData.append('artifact_type', options.artifact_type);

    const response = await api.post<{ artifact: Artifact }>(
      `/projects/${projectId}/artifacts/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.artifact;
  },

  // Get artifacts by type
  async getArtifactsByType(projectId: string, type: ArtifactType): Promise<Artifact[]> {
    const response = await this.getArtifacts(projectId, { artifact_type: type });
    return response.data;
  },

  // Get code artifacts
  async getCodeArtifacts(projectId: string): Promise<Artifact[]> {
    return this.getArtifactsByType(projectId, 'code');
  },

  // Get prototype artifacts
  async getPrototypes(projectId: string): Promise<Artifact[]> {
    return this.getArtifactsByType(projectId, 'prototype');
  },

  // Get artifacts from agent
  async getAgentArtifacts(projectId: string): Promise<Artifact[]> {
    const response = await this.getArtifacts(projectId, { source_type: 'agent' });
    return response.data;
  },

  // Get artifacts from workflow
  async getWorkflowArtifacts(projectId: string): Promise<Artifact[]> {
    const response = await this.getArtifacts(projectId, { source_type: 'workflow' });
    return response.data;
  },

  // ============================================
  // Document-specific methods (artifact_type: 'document')
  // ============================================

  // Get all documents for a project
  async getDocuments(
    projectId: string,
    filters: Omit<ArtifactFilters, 'artifact_type'> = {},
    page = 1,
    perPage = 20
  ): Promise<ArtifactListResponse> {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('per_page', String(perPage));

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });

    // Backend returns { artifacts: [...], pagination: {...} }
    const response = await api.get<{ artifacts: Artifact[]; pagination: { current_page: number; total_pages: number; total_count: number } }>(
      `/projects/${projectId}/artifacts/documents?${params.toString()}`
    );
    // Transform to expected ArtifactListResponse format
    return {
      data: response.data.artifacts || [],
      meta: {
        current_page: response.data.pagination?.current_page || page,
        total_pages: response.data.pagination?.total_pages || 1,
        total_count: response.data.pagination?.total_count || 0,
      },
    };
  },

  // Get document by slug
  async getDocumentBySlug(projectId: string, slug: string): Promise<Artifact> {
    const response = await api.get<{ artifact: Artifact }>(
      `/projects/${projectId}/artifacts/by_slug/${slug}`
    );
    return response.data.artifact;
  },

  // Create document from template
  async createDocumentFromTemplate(
    projectId: string,
    category: DocumentCategory
  ): Promise<Artifact> {
    const response = await api.post<{ artifact: Artifact }>(
      `/projects/${projectId}/artifacts/from_template`,
      { category }
    );
    return response.data.artifact;
  },

  // Update document status
  async updateDocumentStatus(
    projectId: string,
    artifactId: string,
    docStatus: DocumentStatus
  ): Promise<Artifact> {
    const response = await api.patch<{ artifact: Artifact }>(
      `/projects/${projectId}/artifacts/${artifactId}/doc_status`,
      { doc_status: docStatus }
    );
    return response.data.artifact;
  },

  // Submit document for review
  async submitForReview(projectId: string, artifactId: string): Promise<Artifact> {
    return this.updateDocumentStatus(projectId, artifactId, 'review');
  },

  // Approve document
  async approveDocument(projectId: string, artifactId: string): Promise<Artifact> {
    return this.updateDocumentStatus(projectId, artifactId, 'approved');
  },

  // Deprecate document
  async deprecateDocument(projectId: string, artifactId: string): Promise<Artifact> {
    return this.updateDocumentStatus(projectId, artifactId, 'deprecated');
  },

  // Get available document categories with workflow info
  async getDocumentCategories(projectId: string): Promise<DocumentCategoryInfo[]> {
    const response = await api.get<{ categories: DocumentCategoryInfo[] }>(
      `/projects/${projectId}/artifacts/categories`
    );
    return response.data.categories;
  },

  // Get document workflow status for a project
  async getWorkflowStatus(projectId: string): Promise<WorkflowStatusResponse> {
    const response = await api.get<{ workflow_status: WorkflowStatusResponse['workflow_status']; summary: WorkflowStatusResponse['summary'] }>(
      `/projects/${projectId}/artifacts/workflow_status`
    );
    return {
      workflow_status: response.data.workflow_status,
      summary: response.data.summary,
    };
  },

  // Check if a document category can be created
  async canCreateCategory(projectId: string, category: SpecDocumentCategory): Promise<CanCreateResponse> {
    const response = await api.get<CanCreateResponse>(
      `/projects/${projectId}/artifacts/can_create/${category}`
    );
    return response.data;
  },

  // Get document dependencies info
  async getDocumentDependencies(projectId: string, artifactId: string): Promise<DocumentDependenciesResponse> {
    const response = await api.get<DocumentDependenciesResponse>(
      `/projects/${projectId}/artifacts/${artifactId}/dependencies`
    );
    return response.data;
  },

  // ============================================
  // Confirmation Methods
  // ============================================

  // Confirm an essential document
  async confirmArtifact(
    projectId: string,
    artifactId: string,
    note?: string
  ): Promise<{ artifact: Artifact; next_available: Array<{ category: DocumentCategory; title: string }> }> {
    const response = await api.post<{ artifact: Artifact; next_available: Array<{ category: DocumentCategory; title: string }> }>(
      `/projects/${projectId}/artifacts/${artifactId}/confirm`,
      { note }
    );
    return response.data;
  },

  // Unconfirm (cancel confirmation) of an essential document
  async unconfirmArtifact(projectId: string, artifactId: string): Promise<Artifact> {
    const response = await api.delete<{ artifact: Artifact }>(
      `/projects/${projectId}/artifacts/${artifactId}/confirm`
    );
    return response.data.artifact;
  },

  // Get pending confirmation documents
  async getPendingConfirmations(projectId: string): Promise<{ artifacts: Artifact[]; count: number }> {
    const response = await api.get<{ artifacts: Artifact[]; count: number }>(
      `/projects/${projectId}/artifacts/pending_confirmations`
    );
    return response.data;
  },

  // ============================================
  // Helper Methods
  // ============================================

  // Get available artifact types
  getArtifactTypes(): ArtifactType[] {
    return ['code', 'prototype', 'asset', 'config', 'export', 'generated', 'upload', 'document'];
  },

  // Get language icon based on detected language
  getLanguageIcon(language: string | null): string {
    const icons: Record<string, string> = {
      javascript: '📜',
      typescript: '📘',
      python: '🐍',
      ruby: '💎',
      go: '🔷',
      rust: '🦀',
      java: '☕',
      html: '🌐',
      css: '🎨',
      json: '📋',
      yaml: '📄',
      markdown: '📝',
      bash: '💻',
      sql: '🗄️',
    };
    return icons[language || ''] || '📄';
  },

  // Get all document categories with display names and workflow info
  getDocumentCategoriesWithLabels(): Array<{ value: DocumentCategory; label: string; essential: boolean; order: number | null }> {
    return [
      { value: 'prd', label: 'PRD (Product Requirements)', essential: true, order: 1 },
      { value: 'user_flow', label: 'User Flow', essential: true, order: 2 },
      { value: 'design_system', label: 'Design System', essential: true, order: 3 },
      { value: 'trd', label: 'TRD (Technical Requirements)', essential: true, order: 3 },
      { value: 'db_schema', label: 'Database Schema', essential: true, order: 4 },
      { value: 'api_spec', label: 'API Specification', essential: true, order: 4 },
      { value: 'tasks', label: 'Tasks Document', essential: true, order: 5 },
      { value: 'requirements', label: 'Requirements', essential: false, order: null },
      { value: 'architecture', label: 'Architecture', essential: false, order: null },
      { value: 'testing', label: 'Testing Strategy', essential: false, order: null },
      { value: 'deployment', label: 'Deployment Guide', essential: false, order: null },
      { value: 'other', label: 'Other', essential: false, order: null },
    ];
  },

  // Get essential document categories only
  getEssentialCategories(): DocumentCategory[] {
    return ['prd', 'user_flow', 'design_system', 'trd', 'db_schema', 'api_spec', 'tasks'];
  },

  // Get optional document categories
  getOptionalCategories(): DocumentCategory[] {
    return ['requirements', 'architecture', 'testing', 'deployment', 'other'];
  },

  // Check if a category is essential
  isEssentialCategory(category: DocumentCategory): boolean {
    return this.getEssentialCategories().includes(category);
  },

  // Get document status badge color
  getDocumentStatusColor(status: DocumentStatus | null): string {
    const colors: Record<DocumentStatus, string> = {
      draft: 'gray',
      review: 'yellow',
      approved: 'green',
      deprecated: 'red',
    };
    return colors[status || 'draft'];
  },

  // Get document category icon
  getDocumentCategoryIcon(category: DocumentCategory | null): string {
    const icons: Record<DocumentCategory, string> = {
      prd: '📋',
      trd: '🔧',
      api_spec: '🔌',
      db_schema: '🗄️',
      user_flow: '🔄',
      design_system: '🎨',
      tasks: '✅',
      requirements: '📝',
      architecture: '🏗️',
      testing: '🧪',
      deployment: '🚀',
      other: '📄',
    };
    return icons[category || 'other'] || '📄';
  },

  // ============================================
  // Version History Methods
  // ============================================

  // Get version history for an artifact
  async getVersions(
    projectId: string,
    artifactId: string,
    page = 1,
    perPage = 20
  ): Promise<{
    versions: ArtifactVersion[];
    pagination: { current_page: number; total_pages: number; total_count: number };
    currentVersion: number;
  }> {
    const response = await api.get<{
      versions: Array<{
        id: string;
        version_number: number;
        change_summary?: string;
        created_by_name?: string;
        created_at: string;
        additions: number;
        deletions: number;
        formatted_changes?: string;
        relative_time?: string;
      }>;
      pagination: { current_page: number; total_pages: number; total_count: number };
      current_version: number;
    }>(`/projects/${projectId}/artifacts/${artifactId}/versions?page=${page}&per_page=${perPage}`);

    return {
      versions: response.data.versions.map(v => ({
        id: v.id,
        versionNumber: v.version_number,
        changeSummary: v.change_summary,
        createdByName: v.created_by_name,
        createdAt: v.created_at,
        additions: v.additions,
        deletions: v.deletions,
        formattedChanges: v.formatted_changes,
        relativeTime: v.relative_time,
      })),
      pagination: response.data.pagination,
      currentVersion: response.data.current_version,
    };
  },

  // Get a specific version
  async getVersion(
    projectId: string,
    artifactId: string,
    versionNumber: number
  ): Promise<ArtifactVersion> {
    const response = await api.get<{
      version: {
        id: string;
        version_number: number;
        change_summary?: string;
        created_by_name?: string;
        created_at: string;
        additions: number;
        deletions: number;
        formatted_changes?: string;
        relative_time?: string;
        name?: string;
        content?: string;
        checksum?: string;
        file_size?: number;
        metadata?: Record<string, unknown>;
        summary?: string;
        outline?: { level: number; text: string }[];
        doc_status?: DocumentStatus;
        tags?: string[];
      };
    }>(`/projects/${projectId}/artifacts/${artifactId}/versions/${versionNumber}`);

    const v = response.data.version;
    return {
      id: v.id,
      versionNumber: v.version_number,
      changeSummary: v.change_summary,
      createdByName: v.created_by_name,
      createdAt: v.created_at,
      additions: v.additions,
      deletions: v.deletions,
      formattedChanges: v.formatted_changes,
      relativeTime: v.relative_time,
      name: v.name,
      content: v.content,
      checksum: v.checksum,
      fileSize: v.file_size,
      metadata: v.metadata,
      summary: v.summary,
      outline: v.outline,
      docStatus: v.doc_status,
      tags: v.tags,
    };
  },

  // Restore to a specific version
  async restoreVersion(
    projectId: string,
    artifactId: string,
    versionNumber: number
  ): Promise<{ artifact: Artifact; restoredFromVersion: number; message: string }> {
    const response = await api.post<{
      artifact: Artifact;
      restored_from_version: number;
      message: string;
    }>(`/projects/${projectId}/artifacts/${artifactId}/restore/${versionNumber}`);

    return {
      artifact: response.data.artifact,
      restoredFromVersion: response.data.restored_from_version,
      message: response.data.message,
    };
  },
};

// Export ArtifactVersion type for use in other modules
export interface ArtifactVersion {
  id: string;
  versionNumber: number;
  changeSummary?: string;
  createdByName?: string;
  createdAt: string;
  additions: number;
  deletions: number;
  formattedChanges?: string;
  relativeTime?: string;
  // Detailed fields
  name?: string;
  content?: string;
  checksum?: string;
  fileSize?: number;
  metadata?: Record<string, unknown>;
  summary?: string;
  outline?: { level: number; text: string }[];
  docStatus?: DocumentStatus;
  tags?: string[];
}

export default artifactService;

// ============================================
// Artifact Events (for real-time UI updates)
// ============================================

export const ARTIFACT_CREATED_EVENT = 'artifactCreated';
export const ARTIFACT_UPDATED_EVENT = 'artifactUpdated';
export const ARTIFACT_DELETED_EVENT = 'artifactDeleted';

export interface ArtifactEventDetail {
  projectId?: string;
  artifactId?: string;
  artifactType?: ArtifactType;
  category?: DocumentCategory;
}

export function dispatchArtifactCreated(detail?: ArtifactEventDetail) {
  window.dispatchEvent(new CustomEvent(ARTIFACT_CREATED_EVENT, { detail }));
}

export function dispatchArtifactUpdated(detail?: ArtifactEventDetail) {
  window.dispatchEvent(new CustomEvent(ARTIFACT_UPDATED_EVENT, { detail }));
}

export function dispatchArtifactDeleted(detail?: ArtifactEventDetail) {
  window.dispatchEvent(new CustomEvent(ARTIFACT_DELETED_EVENT, { detail }));
}
